import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

import { ConfigService } from '@venecia/services';
import { UrlBuilder } from '@venecia/builders';
import { ApiResponse, RequestOptions, TemplateHelper } from '@venecia/apis/base';

import {
  IdentityDashboardAccountMetrics,
  IdentityDashboardAccountMetricsRaw,
  IdentityDashboardKpi,
  IdentityDashboardKpiRaw,
} from './model/dashboard.model';

@Injectable({ providedIn: 'root' })
export class IdentityDashboardApi {
  private readonly http = inject(HttpClient);
  private readonly configService = inject(ConfigService);

  private urlBuilder?: UrlBuilder;

  protected constructor() {
    this.configService
      .select('http.rest.identity.url')
      .pipe(take(1))
      .subscribe((value: string) => {
        this.urlBuilder = new UrlBuilder(value, '/api/dashboard');
      });
  }

  getKpis(): Observable<ApiResponse<IdentityDashboardKpi>> {
    return this.getByPath<IdentityDashboardKpiRaw>('kpis').pipe(
      map((response) => ({
        ...response,
        body: this.toKpi(response.body),
      }))
    );
  }

  getAccountMetrics(): Observable<ApiResponse<IdentityDashboardAccountMetrics[]>> {
    return this.getByPath<IdentityDashboardAccountMetricsRaw[]>('account-metrics').pipe(
      map((response) => ({
        ...response,
        body: response.body.map((item) => this.toAccountMetrics(item)),
      }))
    );
  }

  getByPath<T>(path: string | number, options?: RequestOptions): Observable<ApiResponse<T>> {
    const url = this.urlBuilder!.build(path);
    const httpOptions = TemplateHelper.buildOptions(options);

    return this.http
      .get<ApiResponse<T>>(url, httpOptions)
      .pipe(
        TemplateHelper.applyRetry(options),
        catchError((err) => TemplateHelper.handleError(err))
      );
  }

  private toKpi(source: IdentityDashboardKpiRaw): IdentityDashboardKpi {
    return {
      totalAccounts: source['total-accounts'],
      totalApplications: source['total-applications'],
      totalUsers: source['total-users'],
      totalClients: source['total-clients'],
    };
  }

  private toAccountMetrics(source: IdentityDashboardAccountMetricsRaw): IdentityDashboardAccountMetrics {
    return {
      accountId: source['account-id'],
      accountName: source['account-name'],
      accountOwner: source['account-owner'],
      totalUsers: source['total-users'],
      totalClients: source['total-clients'],
    };
  }
}
